/*
    Depedencies
*/
import Vue from 'vue';
import Router from 'vue-router';

/*
    Helpers
*/
import isAuthorized, { getRefreshAt, refreshToken, getUser } from '@/helpers/auth';
import redirectBasedOnRole from '@/helpers/router';

/*
    Routes
*/
import adminRoutes from './admin';
import authenticationRoutes from './authentication';
import resellersRoutes from './reseller';
import clientsRoutes from './client';

Vue.use(Router);


const router = new Router({
    mode: 'history',

    routes: [
        ...adminRoutes,
        ...resellersRoutes,
        ...clientsRoutes,
        ...authenticationRoutes,
        {
            path: '/',
            name: 'Home',
            meta: { requiresAuth: true },
            redirect: redirectBasedOnRole(),
        },
        {
            path: '/account',
            name: 'Account',
            meta: { requiresAuth: true },
            component: () => import('@/views/Authentication/Account'),
        },
        {
            path: '/404',
            name: 'NotFound',
            component: () => import('@/views/Authentication/NotFound'),
        },
        {
            path: '/dashboard',
            name: 'Dashboard',
            component: () => import('@/views/Dashboard'),
            children: [
                {
                    path: '/dashboard',
                    name: 'DashboardContent',
                    component: () => import('@/views/Dashboard/Dashboard'),
                },
            ],

        },
        {
            path: '/faq',
            name: 'Faq',
            component: () => import('@/views/Dashboard'),
            children: [
                {
                    path: '/faq',
                    name: 'General faq',
                    component: () => import('@/views/Help/Faq'),
                },
            ],
        },

        {
            path: '/aboutus',
            name: 'about',
            component: () => import('@/views/Dashboard'),
            children: [
                {
                    path: '/aboutus',
                    name: 'General About Us',
                    component: () => import('@/views/About/AboutUs'),
                },
            ],
        },

        {
            path: '/conditions',
            name: 'Conditions',
            component: () => import('@/views/Dashboard'),
            children: [
                {
                    path: '/conditions',
                    name: 'General Conditions',
                    component: () => import('@/views/Conditions/Conditions'),
                },
            ],
        },

        {
            path: '/contact',
            name: 'Contact',
            component: () => import('@/views/Dashboard'),
            children: [
                {
                    path: '/contact',
                    name: 'General Contact Us',
                    component: () => import('@/views/Contact/Contact'),
                },
            ],
        },
        {
            path: '/register',
            name: 'register',
            component: () => import('@/views/Dashboard'),
            children: [
                {
                    path: '/register',
                    name: 'registerContent',
                    component: () => import('@/views/Register/Register'),
                },
            ],
        },
        {
            path: '/payment',
            name: 'payment',
            component: () => import('@/views/Dashboard'),
            children: [
                {
                    path: '/payment/:id',
                    name: 'paymentStatus',
                    component: () => import('@/views/Payment/PaymentStatus'),
                },
            ],
        },
    ],
});

router.beforeEach(async (to, from, next) => {
    NProgress.start();

    if (to.matched.some(record => record.meta.requiresAuth)) {

        if (!isAuthorized()) {
            next({
                path: '/dashboard',
            });

            return;
        }

        const now = new Date().getTime() / 1000;
        const refreshAt = getRefreshAt();

        if (now >= refreshAt) {
            /*
                This will run everytime a user is authenticated
                and is routing through the application. Because
                we route away we would like to kill all network requests
                to refresh the token right after.
            */
            window.stop();
            await refreshToken();
        }
    }

    if (to.matched.some(record => record.meta.checkUserRole)) {
        const user = getUser();

        const types = {
            'admin': 'admin',
            'reseller': 'resellers',
            'client': 'clients',
        };

        const userType = types[user.user_type];
        const path = to.path.split('/')[1];

        if (user.client && user.client.has_minox_exception && to.path !== '/clients/company') {
            next({
                path: '/clients/company',
            });
            return;
        }

        if (user.reseller && user.reseller.has_minox_exception && to.path !== '/resellers/summary') {
            next({
                path: '/resellers/summary',
            });
            return;
        }

        if (path !== userType) {
            next({
                path: `/${userType}`,
            });

            return;
        }
    }

    if (to.matched.some(record => record.meta.checkUserSubbed)) {
        const user = getUser();

        if (user.client && user.client.has_minox_exception && to.path !== '/clients/company') {
            next({
                path: '/clients/company',
            });
            return;
        }

        if (user.reseller && user.reseller.has_minox_exception && to.path !== '/resellers/summary') {
            next({
                path: '/resellers/summary',
            });
            return;
        }

        if (user.client.subscription === null) {
            next({
                path: '/clients/subscription',
            });
        } else {
            const now = new Date().getTime();
            if (user.client.subscription.subscribed_till === null) {
                next();
            } else {
                // add one day to compensate for the same day
                const userSub = new Date(user.client.subscription.subscribed_till).getTime() + 86400000;
                if (userSub < now) {

                    if (from.path === '/clients/subscription') {
                        next(false);
                    } else {
                        next({
                            path: `/clients/subscription`,
                        });
                    }
                } else {
                    next();
                }
            }
        }

        return;
    }

    if (!to.matched.length) {
        next('/404');
    } else {
        next();
    }

    NProgress.done();
});

export default router;
